const MAX_IMG_DIM = 800;
const IMG_QUALITY = 0.7;

/** Vignette persistée EN BASE : bien plus petite que le cache local, car elle
 *  part sur le réseau et vit 12 mois dans l'historique de l'organisation. */
const THUMB_DIM = 320;
const THUMB_QUALITY = 0.6;
/** Garde-fou : au-delà, on n'envoie pas de vignette plutôt que d'alourdir la
 *  base. 120 000 caractères de data-URL ≈ 90 Ko de binaire. */
export const MAX_THUMBNAIL_CHARS = 120_000;

/** Redimensionne et encode en WebP data-URL. Renvoie `null` si l'image est
 *  illisible : un visuel manquant ne doit jamais faire échouer une analyse. */
function toWebpDataUrl(file: File, maxDim: number, quality: number): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const { width, height } = img;
      let w = width, h = height;
      if (w > maxDim || h > maxDim) {
        const ratio = Math.min(maxDim / w, maxDim / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/webp', quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Redimensionne une image (max 800px) et la compresse en WebP data-URL pour
 * persistance légère dans le résultat d'analyse (originalImages). Utilisé pour
 * réafficher les visuels analysés sans re-télécharger les fichiers d'origine.
 */
export function resizeImageForStorage(file: File): Promise<string | null> {
  return toWebpDataUrl(file, MAX_IMG_DIM, IMG_QUALITY);
}

/**
 * Vignette destinée à la BASE (colonne `image_url` de l'analyse), pour que
 * rouvrir une analyse depuis un autre poste — ou après expiration du cache
 * local — réaffiche le visuel soumis et pas seulement sa description.
 *
 * Volontairement plus petite que `resizeImageForStorage` : elle transite par
 * le réseau et vit douze mois. Au-delà de `MAX_THUMBNAIL_CHARS`, on renvoie
 * `null` : mieux vaut aucune vignette qu'une base alourdie.
 */
export async function makeThumbnail(file: File): Promise<string | null> {
  const dataUrl = await toWebpDataUrl(file, THUMB_DIM, THUMB_QUALITY);
  if (!dataUrl || dataUrl.length > MAX_THUMBNAIL_CHARS) return null;
  return dataUrl;
}
